import type { MaintenanceItem } from "../../../../Services/maintenanceApi";
import {
  CalendarDay,
  addMonthsDay,
  dayKey,
  daysBetween,
  toCalendarDay,
  todayDay,
} from "../../../../Utils/calendarDate";

/** Within this many days, an item is "due soon". */
export const DUE_SOON_DAYS = 30;

export type DueState = "overdue" | "due-soon" | "upcoming";

export interface DueInfo {
  state: DueState;
  /** Days until due (negative = overdue). */
  days: number;
  label: string;
}

export const dueInfo = (dueDate: string | null, today: CalendarDay = todayDay()): DueInfo | null => {
  const due = toCalendarDay(dueDate);
  if (!due) return null;
  const days = daysBetween(today, due);
  if (days < 0) {
    const n = -days;
    return { state: "overdue", days, label: n === 1 ? "1 day overdue" : `${n} days overdue` };
  }
  if (days === 0) return { state: "due-soon", days, label: "Due today" };
  if (days === 1) return { state: "due-soon", days, label: "Due tomorrow" };
  if (days <= DUE_SOON_DAYS) return { state: "due-soon", days, label: `Due in ${days} days` };
  const label =
    days < 60 ? `Due in ${Math.round(days / 7)} weeks` : `Due in ${Math.round(days / 30.44)} months`;
  return { state: "upcoming", days, label };
};

/** Active items with a due date, soonest (most overdue) first. */
export const activeByDue = (items: MaintenanceItem[]): MaintenanceItem[] =>
  items
    .filter((i) => i.status === "active" && toCalendarDay(i.dueDate))
    .sort((a, b) => dayKey(toCalendarDay(a.dueDate)!) - dayKey(toCalendarDay(b.dueDate)!));

export const countOverdue = (items: MaintenanceItem[], today: CalendarDay = todayDay()): number =>
  activeByDue(items).filter((i) => dueInfo(i.dueDate, today)?.state === "overdue").length;

export const countDueSoon = (items: MaintenanceItem[], today: CalendarDay = todayDay()): number =>
  activeByDue(items).filter((i) => dueInfo(i.dueDate, today)?.state === "due-soon").length;

export interface CalendarOccurrence {
  item: MaintenanceItem;
  day: CalendarDay;
  /** A future repeat of a recurring item (not its next actual due date). */
  projected: boolean;
}

/**
 * Every occurrence that falls in a month: each active item's next due date,
 * plus — for recurring items — its later repeats, so paging the calendar
 * forward shows the HVAC service again next year. Completed one-offs show on
 * the day they were done.
 */
export const occurrencesInMonth = (
  items: MaintenanceItem[],
  year: number,
  month: number // 0-based
): CalendarOccurrence[] => {
  const out: CalendarOccurrence[] = [];
  const monthStart = year * 12 + month;

  for (const item of items) {
    if (item.status === "completed") {
      const done = toCalendarDay(item.completedAt);
      if (done && done.y === year && done.m === month) out.push({ item, day: done, projected: false });
      continue;
    }
    if (item.status !== "active") continue;
    const due = toCalendarDay(item.dueDate);
    if (!due) continue;

    const dueMonth = due.y * 12 + due.m;
    if (dueMonth === monthStart) {
      out.push({ item, day: due, projected: false });
      continue;
    }
    const interval = item.intervalMonths;
    if (!interval || monthStart < dueMonth) continue;
    const diff = monthStart - dueMonth;
    if (diff % interval !== 0) continue;
    out.push({ item, day: addMonthsDay(due, diff), projected: true });
  }

  return out.sort((a, b) => a.day.d - b.day.d || a.item.title.localeCompare(b.item.title));
};
